// @ts-strict-ignore
import {computed, effect, inject, Injectable, Signal, signal} from "@angular/core";
import {IId, ListData, SearchFilter, SearchParameters} from "@models";
import {SelectionModel} from "@angular/cdk/collections";
import {PageEvent} from "@angular/material/paginator";
import {Sort, SortDirection} from "@angular/material/sort";
import {ActivatedRoute, convertToParamMap, ParamMap, Router} from "@angular/router";
import {map, take} from "rxjs/operators";

const DEFAULT_PAGE_SIZE = 10;
const DEFAULT_PAGE_INDEX = 0;
const DEFAULT_SORT_COLUMN = null;
const DEFAULT_SORT_DIRECTION = "asc";
const DEFAULT_SEARCH_FILTERS: SearchFilter[] = [];

/**
 * Options for the administration table service.
 * Needs to be provided together with the administration table service.
 * For easier usage, there is a provider factory function in `administration-table.providers.ts`.
 */
@Injectable()
export class AdministrationTableServiceOptions {
	persistExpandedElementId?: boolean;
}

/**
 * Shared service for administration table related functionalities.
 * Should be provided in the component that uses the administration table (page component).
 */
@Injectable()
export class AdministrationTableService<T extends IId> {
	private _dataSource = signal<T[]>([]);
	private _isLoading = signal(true);
	private _selection: SelectionModel<T> = new SelectionModel<T>(true, []);
	private _expandedElementId = signal<string | null>(null);
	private _totalElements = signal(0);
	private _currentPageSize = signal(DEFAULT_PAGE_SIZE);
	private _currentPageIndex = signal(DEFAULT_PAGE_INDEX);
	private _sortColumn = signal<string | null>(DEFAULT_SORT_COLUMN);
	private _sortDirection = signal<SortDirection>(DEFAULT_SORT_DIRECTION);
	private _searchFilters = signal<SearchFilter[]>(DEFAULT_SEARCH_FILTERS);
	private _route = inject(ActivatedRoute);
	private _router = inject(Router);
	private _options = inject(AdministrationTableServiceOptions);
	private _hasHydratedFromUrl = {
		pageSize: false,
		pageIndex: false,
		sortColumn: false,
		sortDirection: false,
		searchFilters: false,
	};

	public readonly dataSource = this._dataSource.asReadonly();
	public readonly isLoading = this._isLoading.asReadonly();
	public readonly expandedElementId = this._expandedElementId.asReadonly();
	public readonly totalElements = this._totalElements.asReadonly();
	public readonly currentPageSize = this._currentPageSize.asReadonly();
	public readonly currentPageIndex = this._currentPageIndex.asReadonly();
	public readonly sortColumn = this._sortColumn.asReadonly();
	public readonly sortDirection = this._sortDirection.asReadonly();
	public readonly searchFilters = this._searchFilters.asReadonly();

	public readonly searchParameters: Signal<SearchParameters> = computed(() => {
		return {
			pageSize: this._currentPageSize(),
			pageNumber: this._currentPageIndex(),
			searchFilters: this._searchFilters(),
			sortColumn: this._sortColumn(),
			sortAscending: this._sortDirection() === "asc",
		};
	});

	private _urlPersistenceEffect = effect(() => {
		const searchParameters = this.searchParameters();
		const expandedElementId = this._expandedElementId();
		this._router
			.navigate([], {
				relativeTo: this._route,
				queryParams: {
					pageSize: searchParameters.pageSize,
					pageIndex: searchParameters.pageNumber,
					sortActiveColumn: searchParameters.sortColumn,
					sortDirection: searchParameters.sortAscending ? "asc" : "desc",
					searchColumn: searchParameters.searchFilters.map((filter) => filter.searchColumn),
					searchTerm: searchParameters.searchFilters.flatMap((filter) => filter.searchTerms),
					openedElement: this._options.persistExpandedElementId ? expandedElementId : null,
				},
				queryParamsHandling: "merge", // remove to replace all query params by provided
			})
			.then();
	});

	constructor() {
		// only take the first emitted value and populate the search parameters from it
		this._route.queryParams.pipe(take(1), map(convertToParamMap)).subscribe((params) => {
			this._hydrateFromUrl(params);
		});
	}

	/**
	 * Call this in the constructor of the page component that is providing the service.
	 * This will set the default search parameters for the table.
	 * Parameters parsed from the url will trigger first, so this will only set the default search parameters if the url does not have any.
	 */
	public setDefaultSearchParameters(searchParameters: SearchParameters) {
		if (!this._hasHydratedFromUrl.pageSize) {
			this._currentPageSize.set(searchParameters.pageSize);
		}
		if (!this._hasHydratedFromUrl.pageIndex) {
			this._currentPageIndex.set(searchParameters.pageNumber);
		}
		if (!this._hasHydratedFromUrl.sortColumn) {
			this._sortColumn.set(searchParameters.sortColumn);
		}
		if (!this._hasHydratedFromUrl.sortDirection) {
			this._sortDirection.set(searchParameters.sortAscending ? "asc" : "desc");
		}
		if (!this._hasHydratedFromUrl.searchFilters) {
			this._searchFilters.set(searchParameters.searchFilters);
		}
	}

	// Selection related methods
	public isAllSelected(): boolean {
		return this._selection.hasValue() && this._dataSource().length === this._selection.selected.length;
	}

	public isSomeSelected(): boolean {
		return this._selection.hasValue();
	}

	public isSelected(element: T): boolean {
		return this._selection.isSelected(element);
	}

	public toggleSelection(element: T): void {
		this._selection.toggle(element);
	}

	public toggleAllSelection(): void {
		if (this.isAllSelected()) {
			this._selection.clear();
		} else {
			this._selection.select(...this.dataSource());
		}
	}

	public get selection(): T[] {
		return this._selection.selected;
	}

	// Expand related methods
	public toggleExpandElement(id: string): void {
		this._expandedElementId.update((current) => (current === id ? null : id));
	}

	public isElementExpanded(id: string): boolean {
		return this._expandedElementId() === id;
	}

	// Data related methods
	public setLoading() {
		this._isLoading.set(true);
	}

	public setData(data: ListData<T>) {
		this._dataSource.set(data.data);
		this._totalElements.set(data.totalCount);
		this._isLoading.set(false);
	}

	// Sort related methods
	public sortChanged(sort: Sort) {
		this._selection.clear();
		this._currentPageIndex.set(DEFAULT_PAGE_INDEX);
		this._sortColumn.set(sort.active);
		this._sortDirection.set(sort.direction);
	}

	// Pagination related methods
	public pageChanged($event: PageEvent) {
		this._selection.clear();
		this._currentPageIndex.set($event.pageIndex);
		this._currentPageSize.set($event.pageSize);
	}

	// Search related methods
	public setSearchFilter(filter: SearchFilter[]) {
		// we reset the page index, because the user could be on a different page when applying a new filter
		this._currentPageIndex.set(DEFAULT_PAGE_INDEX);
		this._selection.clear();
		this._searchFilters.set(filter);
	}

	private _hydrateFromUrl(queryParams: ParamMap) {
		if (queryParams.has("pageIndex")) {
			const pageIndex = parseInt(queryParams.get("pageIndex"));
			this._currentPageIndex.set(pageIndex);
			this._hasHydratedFromUrl.pageIndex = true;
		}

		if (queryParams.has("sortActiveColumn")) {
			const sortActiveColumn = queryParams.get("sortActiveColumn");
			this._sortColumn.set(sortActiveColumn);
			this._hasHydratedFromUrl.sortColumn = true;
		}

		if (queryParams.has("sortDirection")) {
			const sortDirection = queryParams.get("sortDirection");
			if (sortDirection === "asc" || sortDirection === "desc" || sortDirection === "") {
				this._sortDirection.set(sortDirection);
				this._hasHydratedFromUrl.sortDirection = true;
			}
		}

		if (queryParams.has("pageSize")) {
			const pageSize = parseInt(queryParams.get("pageSize"));
			this._currentPageSize.set(pageSize);
			this._hasHydratedFromUrl.pageSize = true;
		}

		if (queryParams.has("openedElement")) {
			const openedElement = queryParams.get("openedElement");
			this._expandedElementId.set(openedElement);
		}

		if (queryParams.has("searchTerm") && queryParams.has("searchColumn")) {
			const allSearchColumns = queryParams.getAll("searchColumn");
			const allSearchTerms = queryParams.getAll("searchTerm");
			// just don't hydrate if the search columns and search terms are not the same length.
			// TBD if we want to handle this differently
			if (allSearchColumns.length !== allSearchTerms.length) {
				return;
			}

			const searchFilters: SearchFilter[] = [];
			allSearchColumns.map((searchColumn, index) => {
				searchFilters.push({
					searchColumn: searchColumn,
					searchTerms: [allSearchTerms[index]],
				});
			});
			this._searchFilters.set(searchFilters);
			this._hasHydratedFromUrl.searchFilters = true;
		}
	}

	public refresh() {
		this._selection.clear();
		this._searchFilters.update((current) => [...current]);
	}
}
